import { useEffect, useState } from "react";
import { AlertTriangle, CheckCheck, Loader2, Save, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const cell = "h-7 w-24 rounded border border-slate-200 px-1.5 text-right text-xs tabular-nums focus:border-[#007BFF] focus:outline-none";

/**
 * Shown when a send is blocked by unpriced or zero-margin lines. The estimator prices them right
 * here — material cost, labor hours and margin — takes a vendor quote, or confirms $0 is deliberate.
 */
export default function IncompleteLinesDialog({ open, onOpenChange, estimateId, detail,
                                                onResolved }) {
  const [busy, setBusy] = useState("");
  const [edits, setEdits] = useState({});
  const lines = detail?.lines || [];

  useEffect(() => { if (open) setEdits({}); }, [open, detail]);

  const set = (id, field, value) =>
    setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [field]: value } }));

  const dirty = Object.entries(edits).filter(([, patch]) =>
    Object.values(patch).some((v) => v !== "" && v !== null && v !== undefined));

  const savePrices = async () => {
    setBusy("save");
    try {
      for (const [id, patch] of dirty) {
        const body = {};
        if (patch.unit_cost !== undefined && patch.unit_cost !== "") body.unit_cost = Number(patch.unit_cost);
        if (patch.labor_hours_per_unit !== undefined && patch.labor_hours_per_unit !== "")
          body.labor_hours_per_unit = Number(patch.labor_hours_per_unit);
        if (patch.gm !== undefined && patch.gm !== "") {
          body.material_gm_percent = Number(patch.gm) / 100;
          body.labor_gm_percent = Number(patch.gm) / 100;
        }
        await estApi.updateLine(id, body);
      }
      toast.success(`${dirty.length} line(s) priced`);
      onResolved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save those prices");
    } finally {
      setBusy("");
    }
  };

  const confirmAll = async () => {
    setBusy("confirm");
    try {
      await estApi.bulkLines({
        estimate_id: estimateId, line_ids: lines.map((l) => l.id), cost_confirmed_zero: true,
      });
      toast.success(`${lines.length} line(s) confirmed at $0 — you can send now`);
      onResolved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm those lines");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="incomplete-lines-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-4 w-4" /> This proposal isn't ready to go out
          </DialogTitle>
          <DialogDescription className="text-xs">
            {detail?.message || `${lines.length} line(s) still need a cost or a margin.`} Price them
            below — material cost, labor hours or margin — or confirm that $0 is intentional.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[26rem] overflow-auto rounded border border-slate-200">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Scope</th>
                <th className="px-2 py-1.5 text-left font-medium">Item</th>
                <th className="px-2 py-1.5 text-right font-medium">Material $/unit</th>
                <th className="px-2 py-1.5 text-right font-medium">Labor hrs/unit</th>
                <th className="px-2 py-1.5 text-right font-medium">GM %</th>
                <th className="px-2 py-1.5 text-right font-medium">Line total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className="border-t border-slate-100"
                    data-testid={`incomplete-line-${l.id}`}>
                  <td className="w-36 px-2 py-1.5 text-slate-400">{l.section || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-700">{l.description || "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    <input className={cell} type="number" step="0.01" min="0"
                           placeholder={Number(l.effective_unit_cost || 0).toFixed(2)}
                           value={edits[l.id]?.unit_cost ?? ""}
                           onChange={(e) => set(l.id, "unit_cost", e.target.value)}
                           data-testid={`incomplete-cost-${l.id}`} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input className={cell} type="number" step="0.01" min="0"
                           placeholder={Number(l.labor_hours_per_unit || 0).toFixed(2)}
                           value={edits[l.id]?.labor_hours_per_unit ?? ""}
                           onChange={(e) => set(l.id, "labor_hours_per_unit", e.target.value)}
                           data-testid={`incomplete-hours-${l.id}`} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input className={`${cell} w-16`} type="number" step="1" min="0" max="99"
                           placeholder={Math.round((l.material_gm_used || 0) * 100)}
                           value={edits[l.id]?.gm ?? ""}
                           onChange={(e) => set(l.id, "gm", e.target.value)}
                           data-testid={`incomplete-gm-${l.id}`} />
                  </td>
                  <td className="w-20 px-2 py-1.5 text-right tabular-nums text-amber-700">
                    {money(l.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="incomplete-back">
            <X className="mr-1.5 h-3.5 w-3.5" /> Back to the estimate
          </Button>
          <Button variant="outline" onClick={confirmAll} disabled={!!busy}
                  data-testid="incomplete-confirm-all">
            {busy === "confirm" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              : <CheckCheck className="mr-1.5 h-3.5 w-3.5" />}
            Confirm all {lines.length} as $0
          </Button>
          <Button onClick={savePrices} disabled={!!busy || !dirty.length}
                  data-testid="incomplete-save-prices">
            {busy === "save" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              : <Save className="mr-1.5 h-3.5 w-3.5" />}
            Save {dirty.length || ""} price{dirty.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
